<template>
  <aside
    v-show="isAsideVisible"
    class="aside is-placed-left aside-flex-container"
  >
    <div class="aside-tools">
      <a
        class="navbar-item is-hidden-touch is-hidden-widescreen is-desktop-icon-only"
        @click="asideToggleDesktopOnly"
      >
        <b-icon icon="menu" />
      </a>
      <div
        class="aside-tools-label"
        style="display: flex; align-items: center; justify-content: center; padding: 15px 10px; width: 100%; gap: 10px;"
      >
        <!-- Logo de la consola (XperienceIQ) -->
        <div style="display: flex; align-items: center; justify-content: center; flex: 1;">
          <img
            src="@/assets/logo_experienceiq.png"
            alt="XperienceIQ"
            style="max-height: 40px; max-width: 90%; object-fit: contain;"
          >
        </div>

        <!-- Separador y Logo del cliente -->
        <template v-if="clientLogoUrl">
          <div style="width: 1px; height: 35px; background: rgba(255, 255, 255, 0.2);" />
          <div style="display: flex; align-items: center; justify-content: center; flex: 1;">
            <img
              :src="clientLogoUrl"
              alt="Logo Cliente"
              style="max-height: 40px; max-width: 90%; object-fit: contain;"
            >
          </div>
        </template>
      </div>
    </div>
    <div
      v-if="showClientSelector"
      class="menu"
    >
      <div class="client-selector">
        <b-icon
          icon="account-group"
          size="is-small"
          class="client-icon"
        />
        <select
          class="select-client"
          aria-label="Select client"
          :value="selectedClient"
          @change="onClientChange"
        >
          <option value="">
            Selecciona un client
          </option>
          <option
            v-for="client in clients"
            :key="client.id"
            :value="client.alias"
          >
            {{ client.alias }}
          </option>
        </select>
      </div>
    </div>
    <div class="menu is-menu-main menu-flex-grow">
      <template v-if="loadingMenu">
        <p class="menu-label">
          Loading menu...
        </p>
      </template>
      <template
        v-for="(menuGroup, index) in internalMenu"
        v-else
      >
        <p
          v-if="typeof menuGroup === 'string'"
          :key="`label-${index}`"
          class="menu-label"
        >
          {{ menuGroup }}
        </p>
        <aside-menu-list
          v-else
          :key="`menu-${index}`"
          :menu="menuGroup"
          @menu-click="menuClick"
        />
      </template>
    </div>
    <!-- Botón Descargar App -->
    <div class="aside-app-download aside-footer">
      <router-link
        to="/app-download"
        class="app-download-link"
        :title="$t('menu.descargarApp')"
      >
        <b-icon icon="download" custom-size="default" />
        <span>{{ $t('menu.descargarApp') }}</span>
      </router-link>
    </div>
    <!-- Menú de configuración desplegable (solo para superusuarios) -->
    <div
      v-if="isSuperuser && filteredConfigMenu.length > 0"
      class="aside-config-section aside-footer"
    >
      <div
        class="config-header"
        @click="toggleConfigMenu"
      >
        <b-icon
          icon="cogs"
          custom-size="default"
        />
        <span class="config-label">{{ $t('menu.configuracio') }}</span>
        <b-icon
          :icon="configMenuOpen ? 'chevron-up' : 'chevron-down'"
          custom-size="default"
          class="config-chevron"
        />
      </div>
      <transition name="slide-fade">
        <div
          v-show="configMenuOpen"
          class="config-menu-items"
        >
          <router-link
            v-for="(item, index) in filteredConfigMenu"
            :key="index"
            :to="item.to"
            class="config-menu-item"
          >
            <b-icon
              v-if="item.icon"
              :icon="item.icon"
              custom-size="default"
            />
            <span>{{ item.label }}</span>
          </router-link>
        </div>
      </transition>
    </div>
    <!-- Acciones de usuario fijo abajo -->
    <div class="aside-bottom-actions aside-footer">
      <router-link
        to="/profile"
        class="aside-user-link"
        title="Perfil"
      >
        <user-avatar
          :src="userAvatar"
          size="24"
          class="navbar-avatar"
        />
        <span class="is-user-name">{{ userName }}</span>
      </router-link>
      <a
        class="aside-logout-link"
        title="Cerrar sesión"
        @click="logout"
      >
        <b-icon
          icon="logout"
          custom-size="default"
        />
      </a>
    </div>
  </aside>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState } from 'vuex'
import AsideMenuList from '@/components/AsideMenuList.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import axios from 'axios'
import { API_ROUTES } from '@/axios'
import menuModule, { getConfigMenuItems } from '@/menu'
import { checkToken } from '@/router'

export default defineComponent({
  name: 'AsideMenu',
  components: { AsideMenuList, UserAvatar },
  props: {
    menu: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      config: {}, // Almacena la configuración obtenida
      clientLogoUrl: null, // Logo del cliente
      internalMenu: [],
      loadingMenu: true,
      clients: [], // Array para almacenar clientes
      configMenuOpen: false, // Estado del menú de configuración
      filteredConfigMenu: [] // Elementos de configuración filtrados por permisos
    }
  },
  computed: {
    ...mapState(['isAsideVisible', 'userName', 'userAvatar', 'userId', 'selectedClient', 'isSuperuser', 'isMultiClientAdmin', 'assignedClients', 'userLanguage']),
    showClientSelector () {
      // Mostrar selector si es superusuario O si tiene 2 o más clientes asignados
      return this.isSuperuser || (this.assignedClients && this.assignedClients.length >= 2)
    }
  },
  watch: {
    userId (newVal) {
      if (newVal) {
        this.fetchClientLogo()
      }
    },
    selectedClient (newClient, oldClient) {
      if (newClient && newClient !== oldClient) {
        // Emitir evento para notificar cambio de cliente
        this.$emit('client-changed', newClient)

        // Opcionalmente recargar datos específicos del cliente
        this.$root.$emit('client-selected', newClient)
      }
    },
    clients () {
      // Cuando se cargan los clientes, verificar si hay que sincronizar la selección
      this.$nextTick(() => {
        this.syncClientSelection()
      })
    },
    '$store.state.selectedClient' (newClient) {
      if (newClient && this.clients.length > 0) {
        const clientExists = this.clients.find(client => client.alias === newClient)
        if (!clientExists) {
          this.$store.commit('setSelectedClient', '')
        }
      }
    },
    assignedClients (newClients) {
      // Cuando se cargan los clientes asignados, actualizar la lista local
      if (!this.isSuperuser && newClients && newClients.length > 0) {
        this.clients = newClients
        // También actualizar la lista global de clientes en el store
        this.$store.commit('setClients', newClients)
        if (!this.selectedClient) {
          this.$store.commit('setSelectedClient', newClients[0].alias)
        }
      }
    },
    isSuperuser (newVal) {
      // Cuando cambia el estado de superusuario, recargar clientes
      this.$nextTick(() => {
        this.loadClients()
      })
    },
    userLanguage () {
      // Cuando cambia el idioma, recargar menús para actualizar traducciones
      this.$nextTick(async () => {
        await this.loadMenu()
        await this.loadConfigMenu()
      })
    }
  },
  async mounted () {
    this.loadConfig()
    await this.loadMenu()
    await this.loadConfigMenu()

    // Cargar clientes (ya sea desde el backend para superusuarios o desde assignedClients para usuarios normales)
    await this.loadClients()

    // Inicializar cliente seleccionado
    this.initializeSelectedClient()

    if (this.userId) {
      this.fetchClientLogo()
    }
  },
  methods: {
    async hasPermission (permission) {
      const token = localStorage.getItem('token')
      if (!token) {
        return false
      }

      try {
        const response = await fetch(API_ROUTES.CHECK_PERMISSION(permission), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          const isValidToken = await checkToken()
          if (!isValidToken) {
            localStorage.removeItem('token')
            this.$router.push('/login')
          }
          return false
        }

        const data = await response.json()
        return data.has_permission === true
      } catch (error) {
        const isValidToken = await checkToken()
        if (!isValidToken) {
          localStorage.removeItem('token')
          this.$router.push('/login')
        }
        return false
      }
    },
    async loadConfigMenu () {
      // Filtrar elementos del menú de configuración según permisos
      const filtered = []
      const configMenuItems = getConfigMenuItems()
      for (const item of configMenuItems) {
        if (!item.permission || (await this.hasPermission(item.permission))) {
          filtered.push(item)
        }
      }
      this.filteredConfigMenu = filtered
    },
    toggleConfigMenu () {
      this.configMenuOpen = !this.configMenuOpen
    },
    onClientChange (event) {
      const clientAlias = event.target.value
      this.$store.commit('setSelectedClient', clientAlias)
    },
    asideToggleDesktopOnly () {
      this.$store.dispatch('asideDesktopOnlyToggle')
    },
    menuClick (_item) {
      // Parameter intentionally unused
    },
    async loadConfig () {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(API_ROUTES.CONFIG_CONSOLA, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (response.data) {
          this.config = response.data
        }
      } catch (error) {
        const isValidToken = await checkToken()
        if (!isValidToken) {
          localStorage.removeItem('token')
          this.$router.push('/login')
        }
      }
    },
    isValidImageUrl (url) {
      // Check if URL starts with data:image format
      return url && typeof url === 'string' && url.startsWith('data:image/')
    },
    handleImageError (event) {
      event.target.src = ''
      event.target.style.display = 'none'
    },
    async loadMenu () {
      try {
        this.loadingMenu = true
        if (Array.isArray(this.menu) && this.menu.length > 0) {
          this.internalMenu = this.menu
        } else {
          this.internalMenu = await menuModule.getMenu()
        }
      } catch (error) {
        this.internalMenu = ['', []]
      } finally {
        this.loadingMenu = false
      }
    },
    async loadClients () {
      // Si no es superusuario, usar clientes asignados del store
      if (!this.isSuperuser) {
        if (this.assignedClients && this.assignedClients.length > 0) {
          this.clients = this.assignedClients
          this.$store.commit('setClients', this.assignedClients)
          if (!this.selectedClient) {
            this.$store.commit('setSelectedClient', this.assignedClients[0].alias)
          }
        }
        return
      }

      // Si es superusuario, cargar todos los clientes desde el backend
      const token = localStorage.getItem('token')
      if (!token) {
        return
      }

      try {
        const response = await axios.get(API_ROUTES.CLIENTS_TE_HUB, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (response.data) {
          // No need to filter by actiu, the endpoint already filters by te_hub=True and actiu=True
          this.clients = response.data
            .sort((a, b) => {
              const aliasA = a.alias || a.nombre || `Cliente ${a.id}`
              const aliasB = b.alias || b.nombre || `Cliente ${b.id}`
              return aliasA.localeCompare(aliasB)
            })

          this.$store.commit('setClients', this.clients)

          this.$nextTick(() => {
            this.syncClientSelection()
          })
        }
      } catch (error) {
        const isValidToken = await checkToken()
        if (!isValidToken) {
          localStorage.removeItem('token')
          if (this.$route.path !== '/login') {
            this.$router.push('/login')
          }
        }
      }
    },
    async fetchClientLogo () {
      try {
        if (!this.userId) {
          return
        }

        const token = localStorage.getItem('token')
        if (!token) {
          return
        }

        const response = await axios.get(API_ROUTES.USER_CLINIC_LOGO(this.userId), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        if (response.data && response.data.logo) {
          this.clientLogoUrl = response.data.logo
        } else {
          this.clientLogoUrl = null
        }
      } catch (error) {
        this.clientLogoUrl = null
      }
    },
    async logout () {
      try {
        const token = localStorage.getItem('token')
        if (!token) throw new Error('No token found')
        await axios.post(API_ROUTES.LOGOUT, null, {
          headers: { Authorization: `Bearer ${token}` }
        })
        localStorage.removeItem('token')
        this.$router.push('/login')
      } catch (error) {
        if (this.$buefy && this.$buefy.snackbar) {
          this.$buefy.snackbar.open({
            message: 'Failed to log out',
            type: 'is-danger',
            queue: false
          })
        }
      }
    },
    initializeSelectedClient () {
      // Si no es superusuario y tiene clientes asignados, seleccionar el primero
      if (!this.isSuperuser && this.assignedClients && this.assignedClients.length > 0) {
        if (!this.selectedClient) {
          this.$store.commit('setSelectedClient', this.assignedClients[0].alias)
        }
        return
      }

      // Para superusuarios, usar la lógica original
      const clientFromStorage = localStorage.getItem('selectedClient')
      const clientFromStore = this.$store.state.selectedClient

      const clientToSelect = clientFromStorage || clientFromStore

      if (clientToSelect) {
        this.$store.commit('setSelectedClient', clientToSelect)
      }
    },
    syncClientSelection () {
      const storeClient = this.$store.state.selectedClient

      if (storeClient && this.clients.length > 0) {
        const clientExists = this.clients.find(client => client.alias === storeClient)
        if (clientExists) {
          this.$forceUpdate()
        } else {
          this.$store.commit('setSelectedClient', '')
        }
      }
    }
  }
})
</script>

<style scoped>
/* Contenedor principal del aside con flexbox */
.aside-flex-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--color-principal, #000000);
  border-right: 1px solid #1a1a1a;
}

/* Header del aside con altura mínima */
.aside-tools {
  flex-shrink: 0;
  max-height: 50px;
  background-color: #000000;
  border-bottom: 1px solid #1a1a1a;
  color: #ffffff;
}

/* Menú que ocupa todo el espacio restante */
.menu-flex-grow {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 1rem;
  background-color: var(--color-principal, #000000);
}

/* Footer del aside con altura mínima */
.aside-footer {
  flex-shrink: 0;
}

/* Sección de configuración */
.aside-config-section {
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--color-principal, #050505);
  width: 100%;
}

.config-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--color-texto-aside, #ffffff);
  width: 100%;
  box-sizing: border-box;
}

.config-header:hover {
  background: rgba(255, 255, 255, 0.05);
}

.config-label {
  flex: 1;
  font-weight: 500;
  font-size: 0.95em;
  color: var(--color-texto-aside, #ffffff);
}

.config-chevron {
  margin-left: auto;
  transition: transform 0.3s ease;
  color: var(--color-texto-aside, #cccccc);
}

.config-menu-items {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
  background: rgba(0, 0, 0, 0.3);
}

.config-menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  color: var(--color-texto-aside, #cccccc);
  text-decoration: none;
  transition: all 0.2s;
  font-size: 0.9em;
  width: 100%;
  box-sizing: border-box;
}

.config-menu-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-texto-aside-hover, #ffffff);
  text-decoration: none;
  padding-left: 2.75rem;
}

.config-menu-item.router-link-active {
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-texto-aside-hover, #ffffff);
  font-weight: 600;
  border-left: 3px solid var(--color-detalles, #3273dc);
}

/* Botón Descargar App */
.aside-app-download {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--color-principal, #000000);
  padding: 0.75rem 1rem;
  width: 100%;
  box-sizing: border-box;
}

.app-download-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: #ffffff;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.95em;
  transition: all 0.2s;
  background: var(--color-detalles, #32a4b2);
  border: 1px solid var(--color-detalles, #32a4b2);
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  letter-spacing: 0.02em;
}

.app-download-link:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #ffffff;
  text-decoration: none;
}

.app-download-link .icon {
  font-size: 1.25rem;
}

/* Transición para el desplegable */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  max-height: 300px;
  opacity: 1;
}

.aside-bottom-actions {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--color-principal, #000000);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.aside-user-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0.375rem 0.625rem;
  border-radius: 6px;
  font-size: 0.95em;
  color: var(--color-texto-aside, #ffffff);
  transition: all 0.2s;
  cursor: pointer;
  flex: 1;
  min-width: 0;
}

.aside-user-link:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-texto-aside-hover, #ffffff);
  text-decoration: none;
}

.is-user-name {
  font-weight: 500;
  font-size: 0.9em;
  color: var(--color-texto-aside, #ffffff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.aside-logout-link {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 56, 96, 0.1);
  border: 1px solid rgba(255, 56, 96, 0.3);
  padding: 0.5rem;
  border-radius: 6px;
  color: #ff3860;
  transition: all 0.2s;
  cursor: pointer;
  text-decoration: none;
  min-width: 40px;
  min-height: 40px;
}

.aside-logout-link:hover {
  background: rgba(255, 56, 96, 0.2);
  border-color: #ff3860;
  color: #ff3860;
  text-decoration: none;
  transform: scale(1.05);
}

.navbar-avatar {
  flex-shrink: 0;
}

.navbar-avatar :deep(img) {
  height: 32px !important;
  width: 32px !important;
  min-height: 32px !important;
  min-width: 32px !important;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
}

.client-selector {
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  margin: 0.875rem 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.2s;
}

.client-selector:hover {
  border-color: rgba(255, 255, 255, 0.2);
  background-color: rgba(255, 255, 255, 0.08);
}

.client-icon {
  margin-right: 0.875rem;
  color: var(--color-texto-aside, #aaaaaa);
}

.select-client {
  width: 100%;
  padding: 0.25rem 0;
  border: none;
  background-color: transparent;
  color: var(--color-texto-aside, #ffffff);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23aaa' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: 8px 10px;
  background-position: right center;
  padding-right: 1rem;
}

.select-client:focus {
  outline: none;
  color: var(--color-texto-aside-hover, #ffffff);
}

.select-client option {
  background-color: #1a1a1a;
  color: var(--color-texto-aside, #ffffff);
  border: none;
  padding: 10px;
}

.select-client:hover {
  color: var(--color-texto-aside-hover, #ffffff);
}

/* Responsive Styles for Mobile */
@media (max-width: 768px) {
  .aside-config-section,
  .aside-bottom-actions,
  .aside-app-download {
    /* Ensure these sections are visible in mobile */
    display: flex !important;
  }

  .config-header {
    padding: 1rem 1.25rem;
    min-height: 54px;
  }

  .config-label {
    font-size: 1em;
  }

  .config-menu-item {
    padding: 1rem 1.25rem 1rem 2.75rem;
    min-height: 50px;
    font-size: 0.95em;
  }

  .aside-bottom-actions {
    padding: 1rem 1.25rem;
    gap: 0.75rem;
  }

  .app-download-link {
    padding: 1rem 1.25rem;
    min-height: 54px;
    font-size: 1em;
  }

  .aside-user-link {
    padding: 0.625rem 0.875rem;
    min-height: 48px;
    gap: 0.875rem;
  }

  .navbar-avatar :deep(img) {
    height: 36px !important;
    width: 36px !important;
    min-height: 36px !important;
    min-width: 36px !important;
  }

  .is-user-name {
    font-size: 0.95em;
    max-width: none;
  }

  .aside-logout-link {
    padding: 0.625rem;
    min-height: 48px;
    min-width: 48px;
  }
}

@media (max-width: 480px) {
  .config-header {
    padding: 0.875rem 1rem;
  }

  .config-menu-item {
    padding: 0.875rem 1rem 0.875rem 2.5rem;
  }

  .aside-bottom-actions {
    padding: 0.875rem 1rem;
  }

  .is-user-name {
    font-size: 0.9em;
  }
}
</style>
